import { useState } from 'react';
import { AppError } from '@/domain/ErrorApp.ts';
import { rawLogoff } from '@/shared/hook/usePostLogOff';

interface PostOptions {
  headers?: Record<string, string>;
}

interface PostDataOptions {
  currentUrl?: string;
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  throwOnError?: boolean;
}

export function usePost<REQUEST, RESPONSE>(url: string, options: PostOptions = {}) {
  const [statusCode, setStatusCode] = useState<number>();
  const [data, setData] = useState<RESPONSE | undefined>(undefined);
  const [error, setError] = useState<AppError | undefined>();
  const [loading, setLoading] = useState(false);

  const clearData = () => {
    setData(undefined);
  };

  async function postData(body: REQUEST, postDataOptions: PostDataOptions = {}): Promise<RESPONSE | undefined> {
    const { currentUrl = url, method = 'POST', throwOnError = false } = postDataOptions;
    setLoading(true);
    setError(undefined);

    try {
      const response = await fetch(currentUrl, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        body: body ? JSON.stringify(body) : undefined,
        credentials: 'include',
      });

      setStatusCode(response.status);

      if (response.status === 401 || response.status === 403) {
        await rawLogoff();
        return;
      }

      const text = await response.text(); // 👈 leer como texto primero
      const parsed = text ? JSON.parse(text) : null;

      if (!response.ok) {
        const appErr = new AppError(parsed || { message: 'Error desconocido' });
        setError(appErr);
        if (throwOnError) throw appErr;
        return;
      }

      setData(parsed);
      return parsed;
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      const appErr = error instanceof AppError ? error : new AppError(error);
      setError(appErr);
      if (throwOnError) throw appErr;
    } finally {
      setLoading(false);
    }
  }

  return { data, setData, error, loading, postData, statusCode, clearData };
}
